import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ScriptProvider, useScript } from '../../contexts/ScriptContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/Toast';

/* ─── 공통 상수 ─── */
const POPULAR_KEYWORDS = [
  '다이어트', '피부관리', '탈모', '당뇨', '고혈압',
  '수면장애', '허리통증', '면역력', '갱년기', '우울증',
  '소화불량', '두통', '피로회복', '관절염', '비만',
];

const SECTION_META = {
  intro:     { icon: '🎣', name: '도입부', time: '0:00~2:00' },
  mechanism: { icon: '🔬', name: '기전',   time: '2:00~6:00' },
  case:      { icon: '👨‍👩‍👧', name: '사례',   time: '6:00~9:00' },
  closing:   { icon: '✅', name: '마무리', time: '9:00~12:00' },
};

const SECTION_KEYS = ['intro', 'mechanism', 'case', 'closing'];

/* ─── 스텝 인디케이터 ─── */
function StepIndicator() {
  const { STEPS, currentStep } = useScript();
  return (
    <div style={S.stepBar}>
      {STEPS.map((step, i) => {
        const done = step.num < currentStep;
        const active = step.num === currentStep;
        return (
          <React.Fragment key={step.num}>
            {i > 0 && <div style={{ ...S.stepLine, backgroundColor: done ? '#4CAF50' : '#2A3441' }} />}
            <div style={S.stepItem}>
              <div style={{
                ...S.stepCircle,
                ...(done ? S.stepDone : {}),
                ...(active ? S.stepActive : {}),
              }}>
                {done ? '✓' : step.num}
              </div>
              <span style={{ ...S.stepLabel, color: active || done ? '#4CAF50' : '#8B949E', fontWeight: active ? 600 : 400 }}>
                {step.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ═══════ STEP 1: 키워드 입력 ═══════ */
function Step1() {
  const { keyword, setKeyword, setCurrentStep } = useScript();
  const [input, setInput] = useState(keyword);

  const start = () => { if (input.trim()) { setKeyword(input.trim()); setCurrentStep(2); } };

  return (
    <div style={S.center}>
      <h2 style={S.h2}>어떤 주제로 대본을 만들까요?</h2>
      <p style={S.sub}>키워드를 입력하면 유튜브 + 네이버 데이터를 분석하여 최적의 주제를 추천합니다</p>

      <div style={S.inputWrap}>
        <span style={S.inputIcon}>🔍</span>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && start()}
          placeholder="예: 다이어트, 피부관리, 탈모 예방..."
          style={S.bigInput}
          autoFocus
        />
      </div>

      <div style={{ width: '100%', marginBottom: 36 }}>
        <p style={S.tagLabel}>인기 키워드</p>
        <div style={S.tagGrid}>
          {POPULAR_KEYWORDS.map(kw => (
            <button key={kw} onClick={() => setInput(kw)}
              style={{ ...S.tag, ...(input === kw ? S.tagActive : {}) }}>{kw}</button>
          ))}
        </div>
      </div>

      <button onClick={start} disabled={!input.trim()}
        style={{ ...S.primaryBtn, ...(!input.trim() ? S.btnOff : {}) }}>
        🚀 유튜브 + 네이버 분석 시작
      </button>
    </div>
  );
}

/* ═══════ STEP 2: 분석 + 주제 추천 ═══════ */
function Step2() {
  const { keyword, analysis, setAnalysis, selectedTopic, setSelectedTopic, setCurrentStep } = useScript();
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const called = useRef(false);

  useEffect(() => {
    if (called.current || analysis) return;
    called.current = true;
    setLoading(true);
    setError('');
    api.post('/script/analyze', { keyword })
      .then(r => setAnalysis(r.data))
      .catch(e => setError(e.response?.data?.error || '분석 실패'))
      .finally(() => setLoading(false));
  }, [keyword, analysis, api]);

  if (loading) return (
    <div style={S.center}>
      <div style={S.spinner} />
      <h3 style={{ color: '#E6EDF3', marginTop: 24 }}>"{keyword}" 분석 중...</h3>
      <p style={S.sub}>유튜브 + 네이버 데이터를 수집하고 AI가 최적의 주제를 추천합니다</p>
    </div>
  );

  if (error) return (
    <div style={S.center}>
      <div style={S.errorBox}>{error}</div>
      <button style={S.ghostBtn} onClick={() => { called.current = false; setAnalysis(null); setError(''); }}>다시 시도</button>
    </div>
  );

  if (!analysis) return null;

  const yt = analysis.youtube || {};
  const nv = analysis.naver || {};

  return (
    <div>
      {/* 요약 카드 3개 */}
      <div style={S.statRow}>
        <div style={S.statCard}><div style={S.statNum}>{(yt.count || 0).toLocaleString()}</div><div style={S.statLabel}>유튜브 관련 영상</div></div>
        <div style={S.statCard}><div style={S.statNum}>{(yt.avgViews || 0).toLocaleString()}</div><div style={S.statLabel}>평균 조회수</div></div>
        <div style={S.statCard}><div style={S.statNum}>{(nv.monthlySearch || 0).toLocaleString()}</div><div style={S.statLabel}>네이버 월간 검색량</div></div>
      </div>

      {/* 주제 5개 */}
      <h3 style={{ color: '#E6EDF3', marginBottom: 16 }}>AI 추천 주제</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
        {(analysis.topics || []).map((t, i) => (
          <div key={i} onClick={() => setSelectedTopic(t)}
            style={{ ...S.topicCard, ...(selectedTopic === t ? S.topicSel : {}) }}>
            <div style={S.topicHeader}>
              <span style={S.rank}>#{t.rank}</span>
              <span style={S.kwBadge}>KW {t.kwScore}</span>
              <span style={{ ...S.compBadge, backgroundColor: t.competition === '낮음' ? '#1B5E20' : t.competition === '보통' ? '#E65100' : '#B71C1C' }}>
                {t.competition}
              </span>
            </div>
            <h4 style={S.topicTitle}>{t.title}</h4>
            <p style={S.topicHook}>{t.hook}</p>
            <div style={S.topicFooter}>
              <span>👀 {t.expectedViews}</span>
              <span>🎯 {t.target}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={S.navRow}>
        <button style={S.ghostBtn} onClick={() => { setAnalysis(null); called.current = false; }}>← 다시 분석</button>
        <button style={{ ...S.primaryBtn, ...(selectedTopic ? {} : S.btnOff) }}
          disabled={!selectedTopic} onClick={() => setCurrentStep(3)}>
          선택한 주제로 계속 →
        </button>
      </div>
    </div>
  );
}

/* ═══════ STEP 3: 대본 가이드 ═══════ */
function Step3() {
  const { keyword, selectedTopic, outline, setOutline, guide, setGuide, setBibleRefs, setBibleContext, setCurrentStep } = useScript();
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [localOutline, setLocalOutline] = useState(null);
  const [localGuide, setLocalGuide] = useState(null);
  const [refs, setRefs] = useState([]);
  const called = useRef(false);

  useEffect(() => {
    if (called.current || outline) {
      if (outline) { setLocalOutline({...outline}); setLocalGuide({...guide}); }
      return;
    }
    called.current = true;
    setLoading(true);
    api.post('/script/outline', { topic: selectedTopic, keyword })
      .then(r => {
        setLocalOutline(r.data.outline);
        setLocalGuide(r.data.guide);
        setRefs(r.data.bibleRefDetails || []);
        if (r.data.bibleContext) setBibleContext(r.data.bibleContext);
      })
      .catch(e => setError(e.response?.data?.error || '가이드 생성 실패'))
      .finally(() => setLoading(false));
  }, [keyword, selectedTopic, outline, guide, api]);

  const goNext = () => {
    setOutline(localOutline);
    setGuide(localGuide);
    setBibleRefs(refs);
    setCurrentStep(4);
  };

  if (loading) return (
    <div style={S.center}>
      <div style={S.spinner} />
      <h3 style={{ color: '#E6EDF3', marginTop: 24 }}>대본 가이드 설계 중...</h3>
    </div>
  );

  if (error) return (
    <div style={S.center}>
      <div style={S.errorBox}>{error}</div>
      <button style={S.ghostBtn} onClick={() => { called.current = false; setError(''); }}>다시 시도</button>
    </div>
  );

  if (!localOutline) return null;

  return (
    <div>
      {/* 선택 주제 */}
      <div style={S.selectedTopicBox}>
        <span style={{ fontSize: 13, color: '#8B949E' }}>선택한 주제</span>
        <h3 style={{ color: '#E6EDF3', margin: '4px 0 0' }}>{selectedTopic.title}</h3>
      </div>

      {/* 줄기 6개 */}
      <h3 style={{ color: '#E6EDF3', margin: '24px 0 12px' }}>대본 줄기 (편집 가능)</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
        {SECTION_KEYS.map(key => (
          <div key={key} style={S.outlineCard}>
            <label style={S.outlineLabel}>{SECTION_META[key].icon} {SECTION_META[key].name} ({SECTION_META[key].time})</label>
            <textarea
              value={localOutline[key] || ''}
              onChange={e => setLocalOutline({ ...localOutline, [key]: e.target.value })}
              rows={3}
              style={S.textarea}
            />
          </div>
        ))}
      </div>

      {/* 가이드 4개 */}
      <h3 style={{ color: '#E6EDF3', margin: '24px 0 12px' }}>대본 가이드</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
        {[
          { key: 'tone', label: '톤앤매너' },
          { key: 'target', label: '타겟 시청자' },
          { key: 'emphasis', label: '강조할 포인트' },
          { key: 'forbidden', label: '절대 금지' },
        ].map(g => (
          <div key={g.key} style={S.guideCard}>
            <label style={S.guideLabel}>{g.label}</label>
            <input
              value={localGuide?.[g.key] || ''}
              onChange={e => setLocalGuide({ ...localGuide, [g.key]: e.target.value })}
              style={S.guideInput}
            />
          </div>
        ))}
      </div>

      {/* 바이블 참조 */}
      {refs.length > 0 && (
        <div style={S.bibleBox}>
          <h4 style={{ color: '#4CAF50', margin: '0 0 8px' }}>📖 키성장 바이블 참조 섹션</h4>
          {refs.map(r => (
            <div key={r.id} style={S.bibleRef}><span style={{ color: '#4CAF50' }}>[{r.id}]</span> {r.title}</div>
          ))}
        </div>
      )}

      <div style={S.navRow}>
        <button style={S.ghostBtn} onClick={() => setCurrentStep(2)}>← 주제 선택</button>
        <button style={S.primaryBtn} onClick={goNext}>이 가이드로 대본 생성 →</button>
      </div>
    </div>
  );
}

/* ═══════ STEP 4: 대본 생성 (SSE) ═══════ */
function Step4() {
  const { outline, guide, selectedTopic, keyword, script, setScript, setCurrentStep, bibleContext } = useScript();
  const [streaming, setStreaming] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentSection, setCurrentSection] = useState('');
  const [sections, setSections] = useState({});
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const started = useRef(false);

  useEffect(() => {
    if (started.current || script) { if (script) { setSections(script); setDone(true); } return; }
    started.current = true;
    setStreaming(true);

    const token = localStorage.getItem('accessToken');
    fetch('/api/script/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ outline, guide, topic: selectedTopic, bibleContext: bibleContext || '' }),
    }).then(response => {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      function read() {
        reader.read().then(({ done: rdone, value }) => {
          if (rdone) { setStreaming(false); return; }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'progress') { setProgress(data.percent); setCurrentSection(data.section); }
              else if (data.type === 'section') { setCurrentSection(data.name); }
              else if (data.type === 'content') {
                setSections(prev => ({ ...prev, [data.key]: (prev[data.key] || '') + data.text }));
              }
              else if (data.type === 'done') { setScript(data.script); setDone(true); setStreaming(false); }
              else if (data.type === 'error') { setError(data.message); setStreaming(false); }
            } catch {}
          }
          read();
        });
      }
      read();
    }).catch(e => { setError(e.message); setStreaming(false); });
  }, [outline, guide, selectedTopic, keyword, script, setScript]);

  return (
    <div>
      {/* 진행 바 */}
      <div style={S.progressWrap}>
        <div style={{ ...S.progressBar, width: `${progress}%` }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <span style={{ color: '#8B949E', fontSize: 13 }}>{streaming ? `작성 중: ${currentSection}` : done ? '대본 생성 완료' : ''}</span>
        <span style={{ color: '#4CAF50', fontSize: 13, fontWeight: 600 }}>{progress}%</span>
      </div>

      {error && <div style={S.errorBox}>{error}</div>}

      {/* 섹션별 표시 */}
      {SECTION_KEYS.map(key => {
        const text = sections[key];
        if (!text) return null;
        const meta = SECTION_META[key];
        return (
          <div key={key} style={S.scriptSection}>
            <div style={S.sectionHeader}>
              <span>{meta.icon} {meta.name}</span>
              <span style={{ color: '#8B949E', fontSize: 12 }}>{meta.time}</span>
            </div>
            <div style={S.sectionBody}>{text}</div>
          </div>
        );
      })}

      {done && (
        <div style={{ ...S.navRow, marginTop: 24 }}>
          <button style={S.ghostBtn} onClick={() => setCurrentStep(3)}>← 가이드 수정</button>
          <button style={S.primaryBtn} onClick={() => { setScript(sections); setCurrentStep(5); }}>대본 편집 →</button>
        </div>
      )}
    </div>
  );
}

/* ═══════ STEP 5: 대본 편집 ═══════ */
function Step5() {
  const { script, setScript, setCurrentStep, selectedTopic } = useScript();
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState('');
  const [rewriteKey, setRewriteKey] = useState(null);
  const [rewriteInstruction, setRewriteInstruction] = useState('');
  const [rewriting, setRewriting] = useState(false);

  const startEdit = (key) => { setEditing(key); setEditText(script[key]); setRewriteKey(null); };
  const saveEdit = () => { setScript({ ...script, [editing]: editText }); setEditing(null); };
  const cancelEdit = () => setEditing(null);

  const startRewrite = (key) => { setRewriteKey(key); setRewriteInstruction(''); setEditing(null); };

  const doRewrite = () => {
    setRewriting(true);
    const token = localStorage.getItem('accessToken');
    fetch('/api/script/rewrite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ section: rewriteKey, content: script[rewriteKey], instruction: rewriteInstruction, context: {} }),
    }).then(response => {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let newContent = '';

      function read() {
        reader.read().then(({ done: rdone, value }) => {
          if (rdone) { setRewriting(false); return; }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'content') {
                newContent += data.text;
                setScript(prev => ({ ...prev, [rewriteKey]: newContent }));
              }
              if (data.type === 'done') { setRewriteKey(null); setRewriting(false); }
            } catch {}
          }
          read();
        });
      }
      read();
    }).catch(() => setRewriting(false));
  };

  return (
    <div>
      <h2 style={S.h2}>대본 편집</h2>
      <p style={S.sub}>각 섹션을 직접 수정하거나 AI에게 재작성을 요청하세요</p>

      {SECTION_KEYS.map(key => {
        const meta = SECTION_META[key];
        const isEditing = editing === key;
        const isRewriting = rewriteKey === key;

        return (
          <div key={key} style={S.editSection}>
            <div style={S.editHeader}>
              <div>
                <span style={{ fontSize: 16 }}>{meta.icon} {meta.name}</span>
                <span style={{ color: '#8B949E', fontSize: 12, marginLeft: 8 }}>{meta.time}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {!isEditing && !isRewriting && (
                  <>
                    <button style={S.smallBtn} onClick={() => startEdit(key)}>직접 편집</button>
                    <button style={S.smallBtnGreen} onClick={() => startRewrite(key)}>AI 재작성</button>
                  </>
                )}
              </div>
            </div>

            {isEditing ? (
              <div>
                <textarea value={editText} onChange={e => setEditText(e.target.value)} rows={8} style={{ ...S.textarea, width: '100%' }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button style={S.smallBtnGreen} onClick={saveEdit}>저장</button>
                  <button style={S.smallBtn} onClick={cancelEdit}>취소</button>
                </div>
              </div>
            ) : isRewriting ? (
              <div>
                <div style={S.sectionBody}>{script[key]}</div>
                <div style={{ marginTop: 12 }}>
                  <input
                    value={rewriteInstruction}
                    onChange={e => setRewriteInstruction(e.target.value)}
                    placeholder="추가 지시사항 (예: 더 친근하게, 수치를 더 강조해서...)"
                    style={S.guideInput}
                    onKeyDown={e => e.key === 'Enter' && doRewrite()}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button style={S.smallBtnGreen} onClick={doRewrite} disabled={rewriting}>
                      {rewriting ? '재작성 중...' : 'AI 재작성'}
                    </button>
                    <button style={S.smallBtn} onClick={() => setRewriteKey(null)} disabled={rewriting}>취소</button>
                  </div>
                </div>
              </div>
            ) : (
              <div style={S.sectionBody}>{script[key]}</div>
            )}
          </div>
        );
      })}

      <div style={{ ...S.navRow, marginTop: 24 }}>
        <button style={S.ghostBtn} onClick={() => setCurrentStep(3)}>← 가이드 수정</button>
        <button style={S.primaryBtn} onClick={() => setCurrentStep(6)}>✅ 대본 완성 →</button>
      </div>
    </div>
  );
}

/* ═══════ STEP 6: 완성 + 숏폼 ═══════ */
function Step6() {
  const { script, selectedTopic, keyword, outline, guide, shortforms, setShortforms, savedId, setSavedId, reset } = useScript();
  const { api } = useAuth();
  const { addToast } = useToast();
  const [shortCount, setShortCount] = useState(3);
  const [loadingShort, setLoadingShort] = useState(false);
  const [downloading, setDownloading] = useState('');
  const autoSaved = useRef(false);

  // 자동 저장 (STEP 6 진입 시 1회)
  useEffect(() => {
    if (autoSaved.current || savedId || !script) return;
    autoSaved.current = true;
    api.post('/script/save', { keyword, topic: selectedTopic, outline, guide, script, shortforms })
      .then(r => {
        setSavedId(r.data.id);
        addToast('대본이 보관함에 저장되었습니다', 'success');
      })
      .catch(() => addToast('대본 자동 저장 실패', 'warning'));
  }, [script, savedId, keyword, selectedTopic, outline, guide, shortforms, api, setSavedId, addToast]);

  const download = async (type) => {
    setDownloading(type);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/script/download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ type, script, topic: selectedTopic }),
      });
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      let filename = type === 'shooting' ? '대본_촬영용.docx' : '대본_AI편집용.json';
      const match = cd.match(/filename\*=UTF-8''(.+)/);
      if (match) filename = decodeURIComponent(match[1]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert('다운로드 실패: ' + e.message);
    }
    setDownloading('');
  };

  const genShortforms = async () => {
    setLoadingShort(true);
    try {
      const r = await api.post('/script/shortform', { script, count: shortCount });
      setShortforms(r.data.shortforms || []);
    } catch (e) {
      alert('숏폼 생성 실패: ' + (e.response?.data?.error || e.message));
    }
    setLoadingShort(false);
  };

  return (
    <div>
      <h2 style={S.h2}>대본 완성</h2>
      <p style={S.sub}>{selectedTopic?.title}</p>

      {/* 롱폼 다운로드 */}
      <div style={S.dlRow}>
        <div style={S.dlCard} onClick={() => download('shooting')}>
          <span style={{ fontSize: 32 }}>📝</span>
          <h4 style={{ color: '#E6EDF3', margin: '8px 0 4px' }}>촬영용 대본</h4>
          <p style={{ color: '#8B949E', fontSize: 12, margin: 0 }}>Word(.docx) - 큰 글씨, 큐 포인트</p>
          {downloading === 'shooting' && <p style={{ color: '#4CAF50', fontSize: 12, marginTop: 4 }}>다운로드 중...</p>}
        </div>
        <div style={S.dlCard} onClick={() => download('editing')}>
          <span style={{ fontSize: 32 }}>🤖</span>
          <h4 style={{ color: '#E6EDF3', margin: '8px 0 4px' }}>AI편집용 대본</h4>
          <p style={{ color: '#8B949E', fontSize: 12, margin: 0 }}>JSON - 타임코드, 자막, 컷포인트</p>
          {downloading === 'editing' && <p style={{ color: '#4CAF50', fontSize: 12, marginTop: 4 }}>다운로드 중...</p>}
        </div>
      </div>

      {/* 숏폼 생성 */}
      <div style={{ ...S.card, marginTop: 32 }}>
        <h3 style={{ color: '#E6EDF3', margin: '0 0 16px' }}>숏폼 자동 생성</h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
          <span style={{ color: '#8B949E', fontSize: 14 }}>갯수:</span>
          {[1, 3, 5].map(n => (
            <button key={n} onClick={() => setShortCount(n)}
              style={{ ...S.countBtn, ...(shortCount === n ? S.countBtnActive : {}) }}>{n}개</button>
          ))}
          <button style={S.primaryBtn} onClick={genShortforms} disabled={loadingShort}>
            {loadingShort ? '생성 중...' : `숏폼 ${shortCount}개 생성`}
          </button>
        </div>

        {/* 숏폼 카드 */}
        {shortforms.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {shortforms.map((sf, i) => (
              <ShortformCard key={i} sf={sf} index={i} />
            ))}
          </div>
        )}
      </div>

      <div style={{ ...S.navRow, marginTop: 32 }}>
        <button style={S.ghostBtn} onClick={reset}>🔄 새 대본 만들기</button>
      </div>
    </div>
  );
}

function ShortformCard({ sf }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={S.shortCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ color: '#4CAF50', fontWeight: 700 }}>숏폼 #{sf.index}</span>
          <span style={{ color: '#8B949E', fontSize: 12, marginLeft: 8 }}>{sf.duration}</span>
        </div>
        <button style={S.smallBtn} onClick={() => setExpanded(!expanded)}>
          {expanded ? '접기' : '펼치기'}
        </button>
      </div>
      <p style={{ color: '#E6EDF3', margin: '8px 0 0', fontSize: 14, fontWeight: 500 }}>{sf.hook}</p>
      {expanded && (
        <div style={{ marginTop: 12, padding: 12, backgroundColor: '#0D1117', borderRadius: 8, color: '#C9D1D9', fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
          {sf.content}
        </div>
      )}
    </div>
  );
}

/* ═══════ 메인 ═══════ */
function ScriptContent() {
  const { currentStep, loadSavedScript } = useScript();
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const loadedRef = useRef(false);

  // 보관함에서 "이어 편집" 클릭 시 대본 로드
  useEffect(() => {
    const loadId = searchParams.get('load');
    if (!loadId || loadedRef.current) return;
    loadedRef.current = true;
    api.get(`/script/${loadId}`)
      .then(r => loadSavedScript(r.data))
      .catch(() => {});
  }, [searchParams, api, loadSavedScript]);

  return (
    <div>
      <StepIndicator />
      {currentStep === 1 && <Step1 />}
      {currentStep === 2 && <Step2 />}
      {currentStep === 3 && <Step3 />}
      {currentStep === 4 && <Step4 />}
      {currentStep === 5 && <Step5 />}
      {currentStep === 6 && <Step6 />}
    </div>
  );
}

export default function ScriptPage() {
  return (
    <ScriptProvider>
      <ScriptContent />
    </ScriptProvider>
  );
}

/* ═══════ 스타일 ═══════ */
const S = {
  /* 스텝바 */
  stepBar: { display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 40, padding: '20px 0' },
  stepItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, minWidth: 72 },
  stepCircle: { width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, backgroundColor: '#1E2732', color: '#8B949E', border: '2px solid #2A3441' },
  stepDone: { backgroundColor: '#2E7D32', color: '#fff', borderColor: '#4CAF50' },
  stepActive: { backgroundColor: 'rgba(76,175,80,0.15)', color: '#4CAF50', borderColor: '#4CAF50' },
  stepLine: { width: 40, height: 2, margin: '0 4px', marginBottom: 28 },
  stepLabel: { fontSize: 12, whiteSpace: 'nowrap' },

  /* 공통 */
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 680, margin: '0 auto' },
  h2: { color: '#E6EDF3', fontSize: 28, fontWeight: 700, margin: '0 0 10px', textAlign: 'center' },
  sub: { color: '#8B949E', fontSize: 15, margin: '0 0 32px', lineHeight: 1.6, textAlign: 'center' },
  card: { backgroundColor: '#1E2732', borderRadius: 16, border: '1px solid #2A3441', padding: 24 },

  /* STEP1 */
  inputWrap: { width: '100%', position: 'relative', marginBottom: 32 },
  inputIcon: { position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', fontSize: 22, pointerEvents: 'none' },
  bigInput: { width: '100%', padding: '20px 24px 20px 56px', backgroundColor: '#1E2732', border: '2px solid #4CAF50', borderRadius: 16, color: '#E6EDF3', fontSize: 18, fontWeight: 500, outline: 'none', fontFamily: "'Noto Sans KR',sans-serif", boxSizing: 'border-box', boxShadow: '0 0 20px rgba(76,175,80,0.1)' },
  tagLabel: { color: '#8B949E', fontSize: 13, marginBottom: 12, fontWeight: 500 },
  tagGrid: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  tag: { padding: '8px 18px', backgroundColor: '#1E2732', border: '1px solid #2A3441', borderRadius: 10, color: '#C9D1D9', fontSize: 14, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },
  tagActive: { borderColor: '#4CAF50', color: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.1)' },

  /* 버튼 */
  primaryBtn: { padding: '14px 40px', backgroundColor: '#2E7D32', color: '#fff', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },
  btnOff: { backgroundColor: '#1E2732', color: '#555', cursor: 'not-allowed' },
  ghostBtn: { padding: '12px 24px', backgroundColor: 'transparent', border: '1px solid #2A3441', borderRadius: 10, color: '#8B949E', fontSize: 14, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },
  smallBtn: { padding: '6px 14px', backgroundColor: '#1E2732', border: '1px solid #2A3441', borderRadius: 8, color: '#C9D1D9', fontSize: 12, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },
  smallBtnGreen: { padding: '6px 14px', backgroundColor: '#2E7D32', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },
  navRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },

  /* STEP2 */
  statRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 32 },
  statCard: { backgroundColor: '#1E2732', borderRadius: 16, border: '1px solid #2A3441', padding: 20, textAlign: 'center' },
  statNum: { color: '#4CAF50', fontSize: 28, fontWeight: 700, marginBottom: 4 },
  statLabel: { color: '#8B949E', fontSize: 13 },
  topicCard: { backgroundColor: '#1E2732', borderRadius: 16, border: '1px solid #2A3441', padding: 20, cursor: 'pointer', transition: 'border-color 0.15s' },
  topicSel: { borderColor: '#4CAF50', boxShadow: '0 0 12px rgba(76,175,80,0.15)' },
  topicHeader: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 },
  rank: { color: '#4CAF50', fontWeight: 700, fontSize: 16 },
  kwBadge: { backgroundColor: 'rgba(76,175,80,0.15)', color: '#4CAF50', padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600 },
  compBadge: { color: '#fff', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 },
  topicTitle: { color: '#E6EDF3', fontSize: 17, fontWeight: 600, margin: '0 0 6px' },
  topicHook: { color: '#C9D1D9', fontSize: 14, margin: '0 0 10px', lineHeight: 1.5 },
  topicFooter: { display: 'flex', gap: 16, color: '#8B949E', fontSize: 12 },

  /* STEP3 */
  selectedTopicBox: { backgroundColor: '#1E2732', borderRadius: 12, border: '1px solid #2A3441', padding: '12px 16px' },
  outlineCard: { backgroundColor: '#1E2732', borderRadius: 12, border: '1px solid #2A3441', padding: 16 },
  outlineLabel: { color: '#4CAF50', fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 },
  textarea: { width: '100%', padding: 12, backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 10, color: '#E6EDF3', fontSize: 14, fontFamily: "'Noto Sans KR',sans-serif", resize: 'vertical', outline: 'none', boxSizing: 'border-box' },
  guideCard: { backgroundColor: '#1E2732', borderRadius: 12, border: '1px solid #2A3441', padding: 16 },
  guideLabel: { color: '#C9D1D9', fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 },
  guideInput: { width: '100%', padding: '10px 12px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 10, color: '#E6EDF3', fontSize: 14, fontFamily: "'Noto Sans KR',sans-serif", outline: 'none', boxSizing: 'border-box' },
  bibleBox: { backgroundColor: 'rgba(76,175,80,0.08)', border: '1px solid rgba(76,175,80,0.25)', borderRadius: 12, padding: 16, marginBottom: 24 },
  bibleRef: { color: '#C9D1D9', fontSize: 13, padding: '4px 0' },

  /* STEP4 */
  progressWrap: { height: 6, backgroundColor: '#1E2732', borderRadius: 3, marginBottom: 8, overflow: 'hidden' },
  progressBar: { height: '100%', backgroundColor: '#4CAF50', borderRadius: 3, transition: 'width 0.3s' },
  scriptSection: { backgroundColor: '#1E2732', borderRadius: 16, border: '1px solid #2A3441', marginBottom: 12, overflow: 'hidden' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #2A3441', color: '#4CAF50', fontSize: 14, fontWeight: 600 },
  sectionBody: { padding: 16, color: '#C9D1D9', fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap' },

  /* STEP5 */
  editSection: { backgroundColor: '#1E2732', borderRadius: 16, border: '1px solid #2A3441', padding: 20, marginBottom: 12 },
  editHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, color: '#E6EDF3' },

  /* STEP6 */
  dlRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  dlCard: { backgroundColor: '#1E2732', borderRadius: 16, border: '1px solid #2A3441', padding: 24, textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.15s' },
  countBtn: { padding: '8px 16px', backgroundColor: '#1E2732', border: '1px solid #2A3441', borderRadius: 8, color: '#C9D1D9', fontSize: 14, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },
  countBtnActive: { borderColor: '#4CAF50', color: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.1)' },
  shortCard: { backgroundColor: '#161B22', borderRadius: 12, border: '1px solid #2A3441', padding: 16 },

  /* 기타 */
  errorBox: { backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444', borderRadius: 10, padding: 16, fontSize: 14, textAlign: 'center', marginBottom: 16 },
  spinner: { width: 48, height: 48, border: '4px solid #2A3441', borderTop: '4px solid #4CAF50', borderRadius: '50%', animation: 'spin 1s linear infinite' },
};
